<template>
  <div class="content">
    <div class="col-md-8 ml-auto mr-auto">
      <h2 class="text-center">{{ $t('accountDeletions.title') }}</h2>
      <p class="text-center">
        {{ $t('accountDeletions.description') }}
      </p>
    </div>
    <div class="row mt-5">
      <div class="col-12">
        <card card-body-classes="table-full-width">
          <h4 slot="header" class="card-title">{{ $t('accountDeletions.tableTitle') }}</h4>
          <div>
            <div
              class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap"
            >
              <el-select
                class="select-primary mb-3 pagination-select"
                v-model="pagination.perPage"
                placeholder="Per page"
              >
                <el-option
                  class="select-primary"
                  v-for="item in pagination.perPageOptions"
                  :key="item"
                  :label="item"
                  :value="item"
                >
                </el-option>
              </el-select>

              <div class="row">
                <div class="col-6">
                  <base-input>
                    <el-input
                      type="search"
                      class="mb-3 search-input"
                      prefix-icon="el-icon-search"
                      :placeholder="$t('generic.searchInTablePlaceholder')"
                      v-model="searchQuery"
                      aria-controls="datatables"
                    >
                    </el-input>
                  </base-input>
                </div>
              </div>
            </div>

            <el-table v-loading="loading" :data="queriedData" :empty-text="$t('generic.noData')">
              <el-table-column type="expand">
                <template slot-scope="props">
                  <account-deletion-details :details="props.row"/>
                </template>
              </el-table-column>
              <el-table-column
                v-for="column in tableColumns"
                :key="column.label"
                :min-width="column.minWidth"
                :prop="column.prop"
                :label="$t(column.label)"
                :formatter="column.formatter"
              >
              </el-table-column>
              <el-table-column :label="$t('accountDeletions.account_id')" min-width="180">
                <div slot-scope="{ row }">
                  <router-link :to="accountLinkUrl(row.account_id)">{{row.account_id}}</router-link>
                </div>
              </el-table-column>
            </el-table>
          </div>
          <div
            slot="footer"
            class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap"
          >
            <div class="">
              <p class="card-category">
                {{$t('generic.tablePaginationSummary', {from: from+1, to: to, total: total})}}
              </p>
            </div>
            <base-pagination
              class="pagination-no-border"
              v-model="pagination.currentPage"
              :per-page="pagination.perPage"
              :total="total"
            >
            </base-pagination>
          </div>
        </card>
      </div>
    </div>
  </div
  >
</template>
<script>
  import {Option, Select, Table, TableColumn} from 'element-ui';
  import {BaseAlert, BasePagination} from 'src/components';
  import Fuse from 'fuse.js';
  import {mapActions, mapState} from "vuex";
  import AccountDeletionDetails from 'src/components/AccountDeletion/AccountDeletionDetails';
  import linkUtils from "@/util/link_utils"

  export default {
    name: 'accountDeletions',
    components: {
      BasePagination,
      BaseAlert,
      [Select.name]: Select,
      [Option.name]: Option,
      [Table.name]: Table,
      [TableColumn.name]: TableColumn,
      AccountDeletionDetails
    },
    computed: {
      ...mapState({
        loading: state => state.account_deletions.loading,
      }),
      /***
       * Returns a page from the searched data or the whole data. Search is performed in the watch section below
       */
      queriedData() {
        let result = this.tableData;
        if (this.searchedData.length > 0) {
          result = this.searchedData;
        }
        return result.slice(this.from, this.to);
      },
      to() {
        let highBound = this.from + this.pagination.perPage;
        if (this.total < highBound) {
          highBound = this.total;
        }
        return highBound;
      },
      from() {
        return this.pagination.perPage * (this.pagination.currentPage - 1);
      },
      total() {
        return this.searchedData.length > 0
          ? this.searchedData.length
          : this.tableData.length;
      }
    },
    data() {
      return {
        pagination: {
          perPage: 50,
          currentPage: 1,
          perPageOptions: [5, 10, 25, 50],
          total: 0
        },
        searchQuery: '',
        propsToSearch: ['account_id', 'status'],
        tableColumns: [
          {
            prop: 'type',
            label: 'accountDeletions.type',
          },
          {
            prop: 'created_at',
            label: 'generic.created',
            formatter: (row, column, cellValue, index) => {
              return this.$options.filters.time_no_seconds(cellValue)
            },
          },
          {
            prop: 'updated_at',
            label: 'generic.updated',
            formatter: (row, column, cellValue, index) => {
              return this.$options.filters.time_no_seconds(cellValue)
            },
          },
          {
            prop: 'status',
            label: 'generic.status',
          },
          {
            prop: 'remaining_balance',
            label: 'accountDeletions.remaining_balance',
            formatter: (row, column, cellValue, index) => {
              return cellValue ? this.$options.filters.money(cellValue) : "";
            },
          }
        ],
        tableData: [],
        searchedData: [],
        fuseSearch: null
      };
    },
    methods: {
      ...mapActions(['LoadAccountDeletions']),
      accountLinkUrl(id) {
        return linkUtils.accountProfilePath(this.$router,{account_id: id});
      },
    },
    mounted() {
      // Loading accounts data
      this.LoadAccountDeletions();
    },
    watch: {
      /**
       * Searches through the table data by a given query.
       * NOTE: If you have a lot of data, it's recommended to do the search on the Server Side and only display the results here.
       * @param value of the query
       */
      searchQuery(value) {
        let result = this.tableData;
        if (value !== '') {
          result = this.fuseSearch.search(this.searchQuery);
        }
        this.searchedData = result;
      },
      "$store.state.account_deletions.data"(data) {
        this.tableData = data
        // Fuse search initialization.
        this.fuseSearch = new Fuse(this.tableData, {
          keys: this.propsToSearch,
          threshold: 0.3
        });
      }
    }
  };
</script>
<style>
  .pagination-select,
  .search-input {
    width: 200px;
  }
</style>
